import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";
import api from "services/api";
import moment from "moment";

import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UpStyledLogo from "components/UpStyled/UpStyledLogo";
import ReactPlayer from "react-player";
import Skeleton from "@mui/material/Skeleton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

/**
 * tutorial modal for user first time login
 */
const AILabPageTurorialModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [activeStep, setActiveStep] = useState(0);

    const data = [
      {
        label: "Text-to-Image",
        title: "",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/resources/UpStyled/tutorial-text-to-image.mp4",
        description: (
          <ul>
            <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Prompt:</strong>Design your dream fashion visuals by typing out a prompt of
              what you want to create.
            </SuiTypography>
            <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Result Size:</strong>Use the sliding bar to set the number of fashion design
              images you want to generate.
            </SuiTypography>
            <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Inference Steps:</strong>Control the level of detail our AI generative
              algorithm infers from your prompt for precise fashion design image generation.
            </SuiTypography>
            <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Imagination Level:</strong>Adjust the 'Imagination Level' to determine the
              degree of artistic freedom and originality in your fashion design images.
            </SuiTypography>
            <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Negative Prompt:</strong>Use 'Negative Prompt' to steer the algorithm away
              from specific elements and achieve your ideal fashion images.
            </SuiTypography>
          </ul>
        ),
      },

      {
        label: "InPaint",
        title: "",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/resources/UpStyled/tutorial-inpaint.mp4",
      },
      {
        label: "Image Transformation",
        title: "",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/resources/UpStyled/online_resource/54Oy-Az1QMtXB_958WISX/tutorial-image-transformation2.mp4",
        description: (
          <div>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular", mb: 1 }}>
              Transform your fashion designs with ease - upload your images and let our AI create
              stunning alterations, taking your creativity to new heights.
            </SuiTypography>

            <ul>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Step 1:</strong>Upload your desired fashion image for modification.
              </SuiTypography>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Step 2:</strong>Fill out the prompt with what you want to see in the
                transformed image.
              </SuiTypography>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Step 3:</strong>Use 'Results Size' to set the desired number of images
                generated.
              </SuiTypography>
            </ul>

            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Advanced Options (not required)</strong>
            </SuiTypography>
            <ul>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Inference Steps:</strong>Fine-tune output detail with slider control.
              </SuiTypography>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Imagination Level:</strong>Adjust the amount of creativity.
              </SuiTypography>
              <SuiTypography component="li" variant="h6" sx={{ fontWeight: "regular" }}>
                <strong>Negative Prompt:</strong>Guide the algorithm away from specific elements.
              </SuiTypography>
            </ul>
          </div>
        ),
      },
    ];

    const handleNext = () => {
      if (activeStep < data.length - 1) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    };

    const handleBack = () => {
      if (activeStep > 0) {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth={true}
      >
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

          <SuiBox>
            <Stepper activeStep={activeStep} alternativeLabel>
              {data.map((item, index) => {
                return (
                  <Step key={index}>
                    <StepLabel sx={{ fontSize: 10 }}>{item?.label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>

            <SuiBox
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              {data[activeStep]?.title ? (
                <SuiTypography variant="h4" color="dark">
                  {data[activeStep].title}
                </SuiTypography>
              ) : null}
              <SuiBox sx={{ py: 1, px: 4, width: "100%" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={data[activeStep]?.description ? 5 : 12}>
                    <ReactPlayer
                      controls={true}
                      url={data[activeStep].media}
                      height="auto"
                      width="100%"
                      fallback={
                        <Skeleton
                          animation="wave"
                          height={450}
                          width="100%"
                          variant="rectangular"
                        />
                      }
                      playing={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={data[activeStep]?.description ? 7 : 12}>
                    <SuiBox sx={{ px: 2 }}>{data[activeStep]?.description}</SuiBox>
                  </Grid>
                </Grid>
              </SuiBox>
              {/* <SuiTypography variant="h6" sx={{ fontWeight: "regular" }} color="text">
                  {data[activeStep].description}
                </SuiTypography> */}
            </SuiBox>

            <SuiBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <SuiButton
                color="secondary"
                onClick={() => (activeStep == 0 ? handleClose() : handleBack())}
              >
                {activeStep == 0 ? "Cancel" : "Back"}
              </SuiButton>

              <SuiButton
                color="info"
                onClick={() => (activeStep == data.length - 1 ? handleClose() : handleNext())}
              >
                {activeStep == data.length - 1 ? "Done" : "Next"}
              </SuiButton>
            </SuiBox>
          </SuiBox>
        </DialogContent>
      </Dialog>
    );
  }
);

export default AILabPageTurorialModal;
